import { useEffect, useState, type FormEvent } from 'react'
import { Save, X } from 'lucide-react'
import {
  LIVER_FORM_STATUSES,
  getLiverFormFieldMeta,
  validateLiverForm,
  type LiverFormFieldName,
  type LiverFormValues
} from '../lib/liverForm'
import type { Liver } from '../lib/supabase'

type LiverFormModalProps = {
  open: boolean
  mode: 'create' | 'edit'
  initialLiver?: Liver | null
  loading?: boolean
  error?: string | null
  onClose: () => void
  onSubmit: (values: LiverFormValues) => Promise<void> | void
}

const STATUS_LABEL: Record<LiverFormValues['status'], string> = {
  active: '稼働中',
  inactive: '休眠',
  graduated: '卒業'
}

export function LiverFormModal({
  open,
  mode,
  initialLiver,
  loading = false,
  error,
  onClose,
  onSubmit
}: LiverFormModalProps) {
  const [values, setValues] = useState<LiverFormValues>(() => valuesFromLiver(initialLiver))
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setValues(valuesFromLiver(initialLiver))
      setLocalError(null)
    }
  }, [initialLiver, open])

  if (!open) return null

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const validation = validateLiverForm(values)
    if (!validation.valid) {
      setLocalError(validation.error)
      return
    }
    setLocalError(null)
    try {
      await onSubmit(values)
    } catch {
      // Mutation errors are surfaced through the error prop after React Query updates.
    }
  }

  const title = mode === 'create' ? 'ライバーを追加' : 'ライバーを編集'
  const submitLabel = mode === 'create' ? '追加' : '保存'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/30 p-4">
      <div className="w-full max-w-xl rounded-lg bg-white shadow-xl border border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="閉じる"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-xs font-medium text-gray-600 sm:col-span-2">
              <FieldLabel field="display_name" />
              <input
                value={values.display_name}
                onChange={(event) =>
                  setValues((current) => ({ ...current, display_name: event.target.value }))
                }
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                placeholder="星野ひかる"
                autoFocus
              />
            </label>

            <label className="space-y-1 text-xs font-medium text-gray-600">
              <FieldLabel field="status" />
              <select
                value={values.status}
                onChange={(event) =>
                  setValues((current) => ({
                    ...current,
                    status: event.target.value as LiverFormValues['status']
                  }))
                }
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              >
                {LIVER_FORM_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_LABEL[status]}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1 text-xs font-medium text-gray-600">
              <FieldLabel field="line_user_id" />
              <input
                value={values.line_user_id ?? ''}
                onChange={(event) =>
                  setValues((current) => ({ ...current, line_user_id: event.target.value }))
                }
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                placeholder="U001"
              />
            </label>

            <label className="space-y-1 text-xs font-medium text-gray-600">
              <FieldLabel field="joined_at" />
              <input
                type="date"
                value={values.joined_at ?? ''}
                onChange={(event) =>
                  setValues((current) => ({ ...current, joined_at: event.target.value }))
                }
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </label>

            <label className="space-y-1 text-xs font-medium text-gray-600">
              <FieldLabel field="left_at" />
              <input
                type="date"
                value={values.left_at ?? ''}
                onChange={(event) =>
                  setValues((current) => ({ ...current, left_at: event.target.value }))
                }
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </label>

            <label className="space-y-1 text-xs font-medium text-gray-600 sm:col-span-2">
              <FieldLabel field="avatar_url" />
              <input
                value={values.avatar_url ?? ''}
                onChange={(event) =>
                  setValues((current) => ({ ...current, avatar_url: event.target.value }))
                }
                className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                placeholder="https://..."
              />
            </label>

            <label className="space-y-1 text-xs font-medium text-gray-600 sm:col-span-2">
              <FieldLabel field="notes" />
              <textarea
                value={values.notes ?? ''}
                onChange={(event) =>
                  setValues((current) => ({ ...current, notes: event.target.value }))
                }
                className="mt-1 min-h-24 w-full resize-y rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                placeholder="配信ジャンル、対応時の注意点、近況など"
              />
            </label>
          </div>

          {(localError || error) && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">
              {localError ?? error}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              disabled={loading}
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 rounded-md bg-sky-500 px-3 py-2 text-sm font-medium text-white hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={loading}
            >
              <Save className="h-4 w-4" />
              {loading ? '保存中…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function FieldLabel({ field }: { field: LiverFormFieldName }) {
  const meta = getLiverFormFieldMeta(field)
  const tone = meta.requirement === 'required'
    ? 'bg-rose-50 text-rose-700'
    : 'bg-gray-100 text-gray-500'

  return (
    <span className="flex items-center gap-2">
      <span>{meta.label}</span>
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${tone}`}>
        {meta.requirementLabel}
      </span>
    </span>
  )
}

function valuesFromLiver(liver?: Liver | null): LiverFormValues {
  return {
    display_name: liver?.display_name ?? '',
    line_user_id: liver?.line_user_id ?? '',
    avatar_url: liver?.avatar_url ?? '',
    status: liver?.status ?? 'active',
    joined_at: liver?.joined_at ?? '',
    left_at: liver?.left_at ?? '',
    notes: liver?.notes ?? ''
  }
}
